import axios from 'axios';
import { IDataManager } from './IDataManager';
import { IFirebase } from './IFirebase';
import {
  filterObject
} from './Utils';
import { RequestFilterCondition } from "./Firebase";

export class DataManager implements IDataManager {
  _currentScenario = null;
  _config: any = {};
  _firebase: IFirebase = null;
  _initialized = false;
  _currentEditableScenario = null;

  constructor(public config: any = {}) {
    this._config = {
      ...config
    };
  }

  public async initialize(firebase: IFirebase): Promise<void> {
    this._firebase = firebase;
    this._initialized = true;
  }

  public async getSettings(): Promise<any> {
    if (!this._initialized)
      return null;

    const isLoggedIn = this._firebase.isLoggedIn();
    const uid = this._firebase.getUserAuth().uid;

    if (isLoggedIn) {
      let data = await this._firebase.read({
        path: 'exercisers/{{appName}}/settings/{{settingsid}}',
        keys: {
          appName: this._config.appName,
          settingsid: uid
        }
      });

      return data;
    } else {
      let localAppData = localStorage.getItem(this._config.appName);
      if (localAppData) {
        let localApp = JSON.parse(localAppData);
        return localApp.settings;
      } else {
        return null;
      }
    }
  }

  public async saveSettings(params: any = {}): Promise<any> {
    const {
      data = {},
    } = params

    if (!this._initialized)
      return null;

    const isLoggedIn = this._firebase.isLoggedIn();

    if (isLoggedIn) {
      let response = await axios({
        method: 'POST',
        baseURL: this._config.apiConfig.baseUrl,
        url: '/exercisers/settings',
        data: {
          appId: this._config.appName,
          data: data
        },
        headers: {
          'Authorization': `Bearer ${this._firebase.getUserAuth().accessToken}`,
          "Content-Type": "application/json"
        }
      });

      return response;
    } else {
      let localAppData = localStorage.getItem(this._config.appName);
      let localApp = localAppData ? JSON.parse(localAppData) : {};
      localApp['settings'] = data;
      localAppData = JSON.stringify(localApp);
      localStorage.setItem(this._config.appName, localAppData);
      return data;
    }
  }

  public setScenarioId(id: string): void {
    this._currentScenario = id;
  }

  public async getScenario(id: string): Promise<any> {
    if (!this._initialized)
      return null;

    this.setScenarioId(id);
    
    let response = await axios({
      method: 'GET',
      baseURL: this._config.apiConfig.baseUrl,
      url: `/exercisers/scenario/${this._config.appName}/${this._currentScenario}`
    });

    return response.data ? response.data.data : null;
  }

  public async saveScenario(params: any = {}): Promise<any> {
    const {
      name,
      data = {},
      newScenario
    } = params

    if (!this._initialized)
      return null;

    const isLoggedIn = this._firebase.isLoggedIn();

    if (isLoggedIn) {
      let response = await axios({
        method: 'POST',
        baseURL: this._config.apiConfig.baseUrl,
        url: '/exercisers/scenario',
        data: {
          appId: this._config.appName,
          scenarioId: newScenario ? undefined : name || this._currentEditableScenario || undefined,
          data: data
        },
        headers: {
          'Authorization': `Bearer ${this._firebase.getUserAuth().accessToken}`,
          "Content-Type": "application/json"
        }
      });

      this._currentEditableScenario = response.data && response.data.scenarioId;

      return response;
    }

    return null;
  }

  public async deleteScenario(params: any = {}): Promise<any> {
    const {
      scenarioId
    } = params

    const scenario = scenarioId ? scenarioId : this._currentEditableScenario;

    if (!scenario || !this._initialized)
      return null;

    const isLoggedIn = this._firebase.isLoggedIn();

    if (isLoggedIn) {
      let response = await axios({
        method: 'DELETE',
        baseURL: this._config.apiConfig.baseUrl,
        url: '/exercisers/scenario',
        data: {
          appId: this._config.appName,
          scenarioId: scenario
        },
        headers: {
          'Authorization': `Bearer ${this._firebase.getUserAuth().accessToken}`,
          "Content-Type": "application/json"
        }
      });

      if (response.data.success && scenario == this._currentEditableScenario) {
        this._currentEditableScenario = null
      }
      return response;
    }

    return null;
  }

  public async getScenarios(authorId?: string): Promise<any> {
    if (!this._initialized)
      return null;

    const whereList: RequestFilterCondition[] = [{ fieldName: "AUTHOR_ID", operator: "==", value: authorId }]

    let data = await this._firebase.readWithWhere({
      path: 'exercisers/{{appName}}/scenarios',
      keys: {
        appName: this._config.appName,
      },
      whereList: authorId ? whereList : []
    });

    return data;
  }

  public async getScenariosWithIDs(ids?: string[]): Promise<any> {
    if (!this._initialized)
      return null;

    const whereList: RequestFilterCondition[] = [{ fieldName: "id", operator: "in", value: ids }]

    let data = await this._firebase.readWithWhere({
      path: 'exercisers/{{appName}}/scenarios',
      keys: {
        appName: this._config.appName,
      },
      whereList: ids ? whereList : []
    });

    return data;
  }

  public async getScenariosWithTag(tag: string): Promise<any> {
    if (!this._initialized)
      return null;

    const whereList: RequestFilterCondition[] = [
      { fieldName: "tags", operator: "array-contains", value: tag },
      { fieldName: "AUTHOR_ID", operator: "==", value: "squidex" }
    ]

    let data = await this._firebase.readWithWhere({
      path: 'exercisers/{{appName}}/scenarios',
      keys: {
        appName: this._config.appName,
      },
      whereList: whereList
    });

    return data;
  }

  public async readState(): Promise<any> {
    if (!this._initialized || !this._currentScenario)
      return null;

    const isLoggedIn = this._firebase.isLoggedIn();

    if (isLoggedIn) {
      let data = await this._firebase.read({
        path: 'exercisers/{{appName}}/scenarios/{{scenario}}/states/{{userId}}',
        keys: {
          appName: this._config.appName,
          scenario: this._currentScenario,
        }
      });

      return data;
    } else {
      let localAppData = localStorage.getItem(this._config.appName)
      if (localAppData) {
        let localApp = JSON.parse(localAppData)
        return localApp.states && localApp.states[this._currentScenario]
      } else {
        return null
      }
    }
  }

  public async writeState(state: any = {}): Promise<void> {
    if (!this._initialized || !this._currentScenario)
      return null;

    const isLoggedIn = this._firebase.isLoggedIn();

    if (isLoggedIn) {
      let options: any = {
        path: 'exercisers/{{appName}}/scenarios/{{scenario}}/states/{{userId}}',
        keys: {
          appName: this._config.appName,
          scenario: this._currentScenario
        },
        data: state
      };

      await this._firebase.write(options);
    } else {
      let localAppData = localStorage.getItem(this._config.appName);
      let localApp = localAppData ? JSON.parse(localAppData) : {};
      localApp['states'] = localApp['states'] ? localApp['states'] : {};
      localApp['states'][this._currentScenario] = state;
      localAppData = JSON.stringify(localApp);
      localStorage.setItem(this._config.appName, localAppData);

      this._currentEditableScenario = +new Date();

      return state;
    }
  }

  public async getUserDoc(): Promise<any> {
    if (!this._initialized)
      return null;

    const isLoggedIn = this._firebase.isLoggedIn();

    if (isLoggedIn) {
      let options: any = {
        path: 'users/{{userId}}'
      };
      let data = await this._firebase.read(options);

      return data;
    } else {
      return {};
    }
  }

  public async getSkins(): Promise<any> {
    if (!this._initialized)
      return null;

    const applicationName = this._config.skinsAppName;

    let data = await this._firebase.readWithWhere({
      path: 'skins',
      keys: { appName: this._config.appName },
      whereList: [{ fieldName: "data.applications", operator: "array-contains", value: applicationName }]
    });
    return data;
  }

  public async getSkin(id: string): Promise<any> {
    if (!this._initialized)
      return null;

    let skin = await this._firebase.read({
      path: 'skins/{{skin}}',
      keys: {
        appName: this._config.appName,
        skin: id
      }
    });
    return skin;
  }

  public async getMathProblems(tag: string, difficulty: number, operation: string): Promise<any> {
    if (!this._initialized)
      return null;

    const whereList: RequestFilterCondition[] = [
      { fieldName: "data.tags.iv", operator: "array-contains", value: tag },
      { fieldName: "data.difficulty.iv", operator: "==", value: difficulty },
      { fieldName: "data.operationType.iv", operator: "in", value: [[operation]] },
    ]

    let data = await this._firebase.readWithWhere({
      path: 'exercisers/{{appName}}/equations',
      keys: {
        appName: this._config.appName,
      },
      whereList: whereList
    });

    return data;
  }

  public async getMediaFiles(): Promise<any> {
    if (!this._initialized)
      return null;

    let data = await this._firebase.readWithWhere({
      path: 'exercisers/{{appName}}/media-files',
      keys: {
        appName: this._config.appName,
      },
      whereList: []
    });

    return data;
  }

  public async getWordList() {
    if (!this._initialized)
      return null;

    const response = await axios({
      method: 'GET',
      url: 'https://firebasestorage.googleapis.com/v0/b/' +
        this._config.firebase.storageBucket +
        '/o/exercisers%2F' +
        this._config.appName +
        '%2Fwords.json?alt=media',
      headers: {"Content-Type": "application/json"}
    });

    return {
      total: response.data?.length,
      items: response.data
    };
  }

  public async getStaticTexts() {
    if (!this._initialized)
      return null;

    const response = await axios({
      method: 'GET',
      url: 'https://firebasestorage.googleapis.com/v0/b/' +
        this._config.firebase.storageBucket +
        '/o/exercisers%2F' +
        this._config.appName +
        '%2Fstatic-texts.json?alt=media',
      headers: {"Content-Type": "application/json"}
    });

    return {
      total: response.data?.length,
      items: response.data
    };
  }

}
